if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        
        // Filter images on initial load based on selected variant
        setTimeout(() => {
          this.filterImagesBySelectedColor();
        }, 100);
        
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        // Always filter images by color, even if there's no featured media
        this.filterImagesBySelectedColor();
        
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        // Get selected color variant
        const selectedColorVariant = this.getSelectedColorVariant();

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        const filterImagesByColor = (items, colorName) => {
          if (!colorName) return items;
          
          return items.filter(item => {
            const img = item.querySelector('img');
            if (!img || !img.alt) return false;
            
            // For exact matching, check if the alt text starts with the color name followed by a delimiter
            const altText = img.alt.toLowerCase().trim();
            const colorNameLower = colorName.toLowerCase().trim();
            
            // Check if alt text starts with exact color name followed by space, dash, or end of string
            const exactMatch = altText === colorNameLower || 
                              altText.startsWith(colorNameLower + ' ') ||
                              altText.startsWith(colorNameLower + '-') ||
                              altText.startsWith(colorNameLower + '_');
            
            // Also include images/videos with 'Others' in alt text (common for all colors)
            const isOthers = img.alt.includes('Others');
            
            return exactMatch || isOthers;
          });
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // Filter images based on selected color
          if (selectedColorVariant) {
            // Hide all images first
            mediaGallerySourceItems.forEach(item => {
              item.style.display = 'none';
            });

            // Show only images matching the selected color
            let colorFilteredItems = filterImagesByColor(mediaGallerySourceItems, selectedColorVariant);
            
            // If no matching images found, show first 4 images as default
            if (colorFilteredItems.length === 0) {
              console.log('No matching images found, showing first 4 images as default');
              colorFilteredItems = mediaGallerySourceItems.slice(0, 4);
            }
            
            // Sort filtered items by file number in ALT text (File1, File2, File3, File4)
            colorFilteredItems.sort((a, b) => {
              const getFileNumber = (item) => {
                const img = item.querySelector('img');
                if (!img || !img.alt) return 999; // Put items without alt text at the end
                
                const match = img.alt.match(/file(\d+)/i);
                return match ? parseInt(match[1]) : 999;
              };
              
              return getFileNumber(a) - getFileNumber(b);
            });
            
            colorFilteredItems.forEach(item => {
              item.style.display = '';
            });

            // Update thumbnail gallery if it exists
            const thumbnailGallery = this.querySelector('media-gallery .thumbnail-list');
            if (thumbnailGallery) {
              const thumbnailItems = Array.from(thumbnailGallery.querySelectorAll('.thumbnail-list__item'));
              
              // Hide all thumbnails first
              thumbnailItems.forEach(thumb => {
                thumb.style.display = 'none';
              });

              // Show thumbnails for matching images in correct order
              colorFilteredItems.forEach((colorItem, index) => {
                const mediaId = colorItem.dataset.mediaId;
                const matchingThumbnail = thumbnailItems.find(thumb => 
                  thumb.dataset.target === mediaId
                );
                if (matchingThumbnail) {
                  matchingThumbnail.style.display = '';
                  // Set the order by updating the CSS order property
                  matchingThumbnail.style.order = index;
                  
                  // Optimize thumbnail loading with lower resolution
                  this.optimizeThumbnailLoading(matchingThumbnail);
                }
              });
            }

            // Variant featured image is already set as first, no need to manually set File1
          } else {
            // Show all images if no color is selected
            mediaGallerySourceItems.forEach(item => {
              item.style.display = '';
            });
            
            const thumbnailItems = this.querySelectorAll('media-gallery .thumbnail-list__item');
            thumbnailItems.forEach(thumb => {
              thumb.style.display = '';
              thumb.style.order = ''; // Reset order
            });
          }

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      getSelectedColorVariant() {
        const variantSelects = this.querySelector('variant-selects');
        if (!variantSelects) return null;

        // First try to find the color option by looking for fieldset with legend containing "Color"
        const colorFieldset = variantSelects.querySelector('fieldset .form__label');
        if (colorFieldset && colorFieldset.textContent.toLowerCase().includes('color')) {
          const colorRadio = colorFieldset.parentElement.querySelector('input[type="radio"]:checked');
          if (colorRadio) {
            console.log('Found color via fieldset:', colorRadio.value);
            return colorRadio.value;
          }
        }

        // Try to find select with color option
        const selects = variantSelects.querySelectorAll('select');
        for (let select of selects) {
          const label = select.closest('.product-form__input')?.querySelector('label');
          if (label && label.textContent.toLowerCase().includes('color')) {
            console.log('Found color via select:', select.value);
            return select.value;
          }
        }

        // Fallback: assume first option is color (common in many themes)
        const firstRadio = variantSelects.querySelector('input[type="radio"]:checked');
        if (firstRadio) {
          console.log('Found color via first radio:', firstRadio.value);
          return firstRadio.value;
        }

        const firstSelect = variantSelects.querySelector('select');
        if (firstSelect) {
          console.log('Found color via first select:', firstSelect.value);
          return firstSelect.value;
        }
        
        console.log('No color variant found');
        return null;
      }

      imageMatchesColor(img, colorName) {
        if (!img || !img.alt || !colorName) return false;
        
        const altText = img.alt.toLowerCase().trim();
        const colorNameLower = colorName.toLowerCase().trim();
        
        // Check if alt text starts with exact color name followed by space, dash, or end of string
        return altText === colorNameLower || 
               altText.startsWith(colorNameLower + ' ') ||
               altText.startsWith(colorNameLower + '-') ||
               altText.startsWith(colorNameLower + '_');
      }

      filterImagesBySelectedColor() {
        const selectedColorVariant = this.getSelectedColorVariant();
        const mediaGallerySource = this.querySelector('media-gallery ul');
        
        console.log('Selected color variant:', selectedColorVariant);
        
        if (!mediaGallerySource) {
          console.log('No media gallery found');
          return;
        }
        
        const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
        console.log('Total media items found:', mediaGallerySourceItems.length);
        
        // Debug: Log all image alt texts
        mediaGallerySourceItems.forEach((item, index) => {
          const img = item.querySelector('img');
          console.log(`Image ${index + 1} alt text:`, img?.alt || 'No alt text');
        });
        
        const filterImagesByColor = (items, colorName) => {
          if (!colorName) return items;
          
          return items.filter(item => {
            const img = item.querySelector('img');
            if (!img || !img.alt) return false;
            
            // For exact matching, check if the alt text starts with the color name followed by a delimiter
            const altText = img.alt.toLowerCase().trim();
            const colorNameLower = colorName.toLowerCase().trim();
            
            // Check if alt text starts with exact color name followed by space, dash, or end of string
            const exactMatch = altText === colorNameLower || 
                              altText.startsWith(colorNameLower + ' ') ||
                              altText.startsWith(colorNameLower + '-') ||
                              altText.startsWith(colorNameLower + '_');
            
            // Also include images/videos with 'Others' in alt text (common for all colors)
            const isOthers = img.alt.includes('Others');
            
            console.log(`Testing "${img.alt}" against "${colorName}":`, exactMatch || isOthers);
            return exactMatch || isOthers;
          });
        };

        if (selectedColorVariant) {
          console.log('Filtering images for color:', selectedColorVariant);
          
          // Hide all images first
          mediaGallerySourceItems.forEach(item => {
            item.style.display = 'none';
          });

          // Show only images matching the selected color
          let colorFilteredItems = filterImagesByColor(mediaGallerySourceItems, selectedColorVariant);
          console.log('Filtered items count:', colorFilteredItems.length);
          
          // If no matching images found, show first 4 images as default
          if (colorFilteredItems.length === 0) {
            console.log('No matching images found, showing first 4 images as default');
            colorFilteredItems = mediaGallerySourceItems.slice(0, 4);
          }
          
          // Sort filtered items by file number in ALT text (File1, File2, File3, File4)
          colorFilteredItems.sort((a, b) => {
            const getFileNumber = (item) => {
              const img = item.querySelector('img');
              if (!img || !img.alt) return 999; // Put items without alt text at the end
              
              const match = img.alt.match(/file(\d+)/i);
              return match ? parseInt(match[1]) : 999;
            };
            
            return getFileNumber(a) - getFileNumber(b);
          });
          
          colorFilteredItems.forEach(item => {
            item.style.display = '';
          });

          // Update thumbnail gallery if it exists
          const thumbnailGallery = this.querySelector('media-gallery .thumbnail-list');
          if (thumbnailGallery) {
            const thumbnailItems = Array.from(thumbnailGallery.querySelectorAll('.thumbnail-list__item'));
            
            // Hide all thumbnails first
            thumbnailItems.forEach(thumb => {
              thumb.style.display = 'none';
            });

            // Show thumbnails for matching images in correct order
            colorFilteredItems.forEach((colorItem, index) => {
              const mediaId = colorItem.dataset.mediaId;
              const matchingThumbnail = thumbnailItems.find(thumb => 
                thumb.dataset.target === mediaId
              );
              if (matchingThumbnail) {
                matchingThumbnail.style.display = '';
                // Set the order by updating the CSS order property
                matchingThumbnail.style.order = index;
                
                // Optimize thumbnail loading with lower resolution
                this.optimizeThumbnailLoading(matchingThumbnail);
              }
            });
          }

          // Variant featured image is already set as first, no need to manually set File1
        } else {
          // Show all images if no color is selected
          mediaGallerySourceItems.forEach(item => {
            item.style.display = '';
          });
          
          const thumbnailItems = this.querySelectorAll('media-gallery .thumbnail-list__item');
          thumbnailItems.forEach(thumb => {
            thumb.style.display = '';
            thumb.style.order = ''; // Reset order
          });
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      optimizeThumbnailLoading(thumbnailElement) {
        if (!thumbnailElement) return;
        
        const img = thumbnailElement.querySelector('img');
        if (!img || img.dataset.optimized) return; // Skip if already optimized
        
        // Mark as optimized to avoid duplicate processing
        img.dataset.optimized = 'true';
        
        // Get the current src and create a lower resolution version
        const currentSrc = img.src;
        if (currentSrc && currentSrc.includes('shopifycdn.com')) {
          // Replace high resolution parameters with lower ones for faster loading
          let lowResSrc = currentSrc
            .replace(/width=\d+/g, 'width=104')
            .replace(/&c=\d+/g, '&c=104');
            
          // If no width parameter exists, add one
          if (!lowResSrc.includes('width=')) {
            lowResSrc = lowResSrc.includes('?') 
              ? lowResSrc + '&width=104' 
              : lowResSrc + '?width=104';
          }
          
          // Preload the low-res image
          const lowResImg = new Image();
          lowResImg.onload = () => {
            // Replace the src with low-res version temporarily
            if (img.src === currentSrc) {
              img.src = lowResSrc;
              
              // Then load the high-res version in background
              const highResImg = new Image();
              highResImg.onload = () => {
                if (img.src === lowResSrc) {
                  img.src = currentSrc;
                }
              };
              highResImg.src = currentSrc;
            }
          };
          lowResImg.src = lowResSrc;
        }
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
